import { Component, computed, inject, Signal, ViewChild } from '@angular/core';
import { HelpService } from './help.service';
import { HelpDTO } from './help.dtos';
import { HelpElementResultComponent } from './help-element-result/help-element-result.component';
import { Store } from '@ngrx/store';
import { State } from '../../../_infrastructure/adapters/store/store.interface';

import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { UserService } from '../../../user.service';
import { ActionDefinitionDTO } from '../../../api/plyca-dtos-v2';
import { ActionsProcessComponent } from '../../../actions/actions.process.component';
import { PlycaDataService } from '../../../../services/plyca.service';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { BaseComponent } from '../baseComponent/base.component';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, filter, tap } from 'rxjs/operators';
import { RoutingPaths } from '@/app/common/routing';
import { throwError } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { ScreenService } from '@/services/screen/screen.service';
import { HelpUseCase } from '@/app/_core/application/help/help.use-cases';
import { WaitLoadingService } from '../waitLoading/wait-loading.service';

@Component({
  selector: 'plyca-helpIndex',
  templateUrl: 'helpIndex.component.html',
  standalone: true,
  imports: [FormsModule, InputTextModule, ButtonModule, OverlayPanelModule, ScrollPanelModule, NgIf, HelpElementResultComponent, TranslateModule]
})
export class HelpIndexComponent extends BaseComponent {
  @ViewChild('op') topNavHelpPanel: OverlayPanel;

  private readonly _helpService = inject(HelpService);
  private readonly _helpUseCases = inject(HelpUseCase);
  private readonly _waitService = inject(WaitLoadingService);
  private readonly _deviceDetectorService = inject(ScreenService);

  public groupedHelpSig: Signal<any[]> = computed(() => []);
  public readonly isDesktop: Signal<boolean> = computed(() => this._deviceDetectorService.isDesktopSig());

  public searchText = '';
  public help: HelpDTO;
  public searching: boolean;

  constructor(
    public userService: UserService,
    public plycaService: PlycaDataService,
    public dialogService: DialogService,
    private router: Router,
    protected store: Store<State>
  ) {
    super();
    // limpiar ayuda contextual al navegar a un nuevo componente
    this.subscribe(router.events.pipe(filter((event) => event instanceof NavigationEnd)), (event: NavigationEnd) => {
      if (this.topNavHelpPanel) {
        this.topNavHelpPanel.hide();
      }
    });
  }

  search(event, overlaypanel: OverlayPanel) {
    const isGeneralHelpRoute = this.router.url.includes(RoutingPaths.LoggedInGeneralHelp);
    if (isGeneralHelpRoute) return;

    console.log('ABRIENDO AYUDA', this.searchText);
    const isSupportUser = this.userService.hasRoles('50');
    if (this.searchText) {
      if (isSupportUser && this.isProjectIdentifier(this.searchText)) {
        const identifier = this.getProjectIdentifier(this.searchText);
        this.router.navigate(['dossierInfo/' + identifier], {});
      } else if (isSupportUser && this.searchText.startsWith(':')) {
        const command = this.searchText.substring(1).trim().toUpperCase();
        this.subscribe(this.plycaService.getAction(command), (datos: any) => {
          if (datos && datos.content) {
            const actionDef = datos.content[0];
            if (actionDef) {
              this.execAction(actionDef);
            }
          }
        });
      } else {
        this.searching = true;
        this.groupedHelpSig = computed(() => this._helpService.groupHelp([]));
        this._waitService.startWaiting();
        this.subscribe(
          this._helpService.searchByIndex(this.searchText).pipe(
            tap((help: HelpDTO) => {
              this.help = help;
              this._waitService.cancelWaiting();
              const dataSig = this._helpUseCases.selectHelpSig();
              this.groupedHelpSig = computed(() => this._helpService.groupHelp(help?.content ?? []));
              this._helpUseCases.setHelp({
                ...dataSig,
                isOpenHelp: true,
                isGeneralHelp: false,
                isRefresh: false,
                isCloseHelp: false,
                extraData: { searchTags: [dataSig.tagByActualView], groupedHelp: this.groupedHelpSig() },
                listData: help?.content ?? [],
                context: null,
                currentTag: ''
              });
              if (!this.isDesktop()) {
                overlaypanel.show(event);
              }
              this.searching = false;
            }),
            catchError((error) => {
              this.searching = false;
              this._waitService.cancelWaiting();
              return throwError(error);
            })
          )
        );
      }
    }
  }

  isProjectIdentifier(command: string) {
    if (command == null) {
      return false;
    }
    command = command.trim();

    if (!command.startsWith('20')) {
      return false;
    }

    if (command.length !== 15 || command.charAt(4) !== '/' || command.split('/').length !== 2) {
      return false;
    }

    const year = command.substring(0, 4);
    const numericRest = command.substring(9);
    if (isNaN(Number(year)) || isNaN(Number(numericRest))) {
      return false;
    }

    return true;
  }

  getProjectIdentifier(command: string) {
    command = command.trim();
    return command.substring(0, 4) + command.substring(5);
  }

  execAction(actionDef: ActionDefinitionDTO) {
    const action = actionDef.executableAction;

    const config: DynamicDialogConfig = {
      header: action.name,
      width: '40%',
      styleClass: 'actionStyle',
      style: { overflow: 'hidden' }
    };
    config['action'] = action;

    return this.dialogService.open(ActionsProcessComponent, config);
  }
}
